import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchPushHealth, type AdminPushHealth } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/** A15.17 — push-provayder holati: FCM sozlangan-mi, ro'yxatdan o'tgan tokenlar,
 *  provayder va e'lon-holati bo'yicha taqsimot. Read-only. */
export function PushHealth() {
  const t = useT()
  const { can } = useAuth()
  const [h, setH] = useState<AdminPushHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchPushHealth(ac.signal)
      .then((r) => {
        if (alive) setH(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader title={t.broadcast.pushHealth} screenId="A15.17" description={t.broadcast.pushHealthNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !h ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="text-sm text-secondary">{t.broadcast.fcm}</div>
              <div className="mt-2">
                <StatusBadge status={h.fcm_configured ? 'active' : 'deleted'} />
                <span className="ml-2 text-sm text-primary">
                  {h.fcm_configured ? t.broadcast.fcmYes : t.broadcast.fcmNo}
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="text-sm text-secondary">{t.broadcast.pushTokens}</div>
              <div className="mt-1 text-2xl font-semibold text-primary">{h.push_tokens.toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-2 text-sm text-secondary">{t.broadcast.byProvider}</div>
              {Object.keys(h.by_provider).length === 0 ? (
                <div className="text-sm text-tertiary">—</div>
              ) : (
                <ul className="space-y-1">
                  {Object.entries(h.by_provider).map(([p, n]) => (
                    <li key={p} className="flex justify-between text-sm">
                      <span className="text-secondary">{p}</span>
                      <span className="font-mono text-primary">{n.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-2 text-sm text-secondary">{t.broadcast.statusBreakdown}</div>
              {Object.keys(h.broadcast_status).length === 0 ? (
                <div className="text-sm text-tertiary">—</div>
              ) : (
                <ul className="space-y-1">
                  {Object.entries(h.broadcast_status).map(([s, n]) => (
                    <li key={s} className="flex justify-between text-sm">
                      <span className="text-secondary">{t.broadcast[s as 'draft'] ?? s}</span>
                      <span className="font-mono text-primary">{n.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
